import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Animate } from "../../components/animate";
import { Layout } from "../../components/layout";
import { client } from "../../lib/sanityClient";
import { jobPostsCountQuery, paginatedJobPostsQuery } from "../../lib/queries";

const JOBS_PER_PAGE = 20;

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-NG", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const JobCard = ({ job, index }) => (
  <Animate animation="fade-up" delay={(index % 3) * 0.1}>
    <article className="flex h-full flex-col rounded-[24px] border border-grey-200 bg-white p-5 shadow-[0_8px_30px_rgba(0,30,81,0.04)] md:p-7">
      <div className="flex flex-1 flex-col gap-5">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-wider">
            {job.employmentType && (
              <span className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                {job.employmentType}
              </span>
            )}
            {job.remote && (
              <span className="rounded-full bg-green/10 px-3 py-1 text-green">
                Remote
              </span>
            )}
          </div>
          <h2 className="text-2xl font-medium leading-tight tracking-[-0.02em] text-deep-primary">
            {job.title}
          </h2>
          <div className="flex flex-wrap gap-x-2 gap-y-1 text-sm text-grey-500">
            {job.company && <span>{job.company}</span>}
            {job.location && <span>· {job.location}</span>}
          </div>
        </div>

        <p className="text-base leading-7 text-grey-600">
          Join MyCard Global and help us build simpler, safer financial tools.
        </p>
      </div>

      <div className="mt-7 flex flex-col gap-4 border-t border-grey-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-grey-500">
          {job.publishedAt && `Posted ${formatDate(job.publishedAt)}`}
        </div>
        <Link
          to={`/careers/${job._id}`}
          className="inline-flex w-fit items-center justify-center rounded-full bg-primary px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-deep-primary"
        >
          Apply{" "}
          <span className="ml-2" aria-hidden="true">
            ↗
          </span>
        </Link>
      </div>
    </article>
  </Animate>
);

const JobSkeleton = () => (
  <div className="animate-pulse rounded-[24px] border border-grey-200 bg-white p-5 md:p-7">
    <div className="mb-4 h-5 w-32 rounded bg-grey-100" />
    <div className="mb-3 h-8 w-3/4 rounded bg-grey-100" />
    <div className="mb-8 h-4 w-1/2 rounded bg-grey-100" />
    <div className="mb-3 h-4 w-full rounded bg-grey-100" />
    <div className="mb-3 h-4 w-5/6 rounded bg-grey-100" />
    <div className="h-10 w-36 rounded-full bg-grey-100" />
  </div>
);

export const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [pageCursors, setPageCursors] = useState([null]);

  useEffect(() => {
    const cursor = pageCursors[currentPage - 1];
    const queryParams = {
      pageSize: JOBS_PER_PAGE,
      lastPublishedAt: cursor?.publishedAt || null,
      lastId: cursor?.id || null,
      test: process.env.NODE_ENV === "development",
    };

    Promise.all([
      client.fetch(paginatedJobPostsQuery, queryParams),
      currentPage === 1
        ? client.fetch(jobPostsCountQuery, {
            test: process.env.NODE_ENV === "development",
          })
        : Promise.resolve(0),
    ])
      .then(([data, count]) => {
        setJobs(data || []);
        if (currentPage === 1) setTotalJobs(count || 0);
      })
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [currentPage, pageCursors]);

  const totalPages = Math.ceil(totalJobs / JOBS_PER_PAGE);

  const changePage = (page) => {
    if (page < 1 || (totalPages > 0 && page > totalPages)) return;
    if (page > currentPage && jobs.length > 0) {
      const lastJob = jobs[jobs.length - 1];
      setPageCursors((cursors) => {
        const nextCursors = [...cursors];
        nextCursors[page - 1] = {
          publishedAt: lastJob.publishedAt,
          id: lastJob._id,
        };
        return nextCursors;
      });
    }
    setLoading(true);
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Layout>
      <div className="w-full">
        <section className="bg-light-yellow py-[64px] lg:py-[80px]">
          <div className="max-width">
            <Animate animation="fade-down">
              <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-grey-500">
                Careers
              </p>
              <h1 className="max-w-[760px] text-[48px] font-medium leading-[52px] tracking-[-0.03em] text-deep-primary lg:text-[72px] lg:leading-[76px]">
                Build what&apos;s next with{" "}
                <span className="font-normal italic text-primary">us.</span>
              </h1>
              <p className="mt-5 max-w-[560px] text-base text-grey-600 lg:text-lg">
                Join a thoughtful team making everyday financial tools simpler,
                safer, and more useful for everyone.
              </p>
            </Animate>
          </div>
        </section>

        <section className="py-[56px] lg:py-[80px]">
          <div className="max-width">
            {loading ? (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {[0, 1].map((item) => (
                  <JobSkeleton key={item} />
                ))}
              </div>
            ) : totalJobs === 0 ? (
              <div className="flex flex-col items-center justify-center py-[64px] text-center">
                <p className="text-2xl font-medium text-deep-primary">
                  No open roles right now
                </p>
                <p className="mt-3 max-w-[360px] text-base text-grey-500">
                  We&apos;re growing carefully. Check back soon for new
                  opportunities to join MyCard Global.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-8 flex items-center justify-between gap-4">
                  <p className="text-sm text-grey-500">
                    Showing {(currentPage - 1) * JOBS_PER_PAGE + 1} to{" "}
                    {Math.min(
                      (currentPage - 1) * JOBS_PER_PAGE + jobs.length,
                      totalJobs,
                    )}{" "}
                    of {totalJobs} roles
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {jobs.map((job, index) => (
                    <JobCard key={job._id} job={job} index={index} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <nav
                    className="mt-10 flex items-center justify-center gap-2"
                    aria-label="Job post pages"
                  >
                    <button
                      type="button"
                      onClick={() => changePage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="rounded-full border border-grey-300 px-4 py-2 text-sm font-medium text-deep-primary transition-colors hover:border-primary disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Previous
                    </button>
                    <span className="px-3 text-sm text-grey-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => changePage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="rounded-full border border-grey-300 px-4 py-2 text-sm font-medium text-deep-primary transition-colors hover:border-primary disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Next
                    </button>
                  </nav>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
};
